import { Children, isValidElement, cloneElement, type CSSProperties, type ReactNode } from "react";
import { cn } from "./cn";
import { Ikon, type IkonAdi } from "./Ikon";
import { marka } from "./marka";
import type { NotTonu } from "./NotKutusu";

/**
 * GİRİŞ KABUĞU — kimlik ekranlarının ortak iki sütunlu düzeni
 *
 * ================================================================
 * ÜÇ EKRAN, TEK KABUK
 * ================================================================
 * Şifre · doğrulama kodu · kurtarma kodu. Çizimde üçünün de SOL
 * PANELİ birebir aynı (`Giris.dc.html`, `GirisDogrulama.dc.html`,
 * `GirisKurtarmaKodu.dc.html` — üç dosyanın sol sütunu karakter
 * karakter özdeş). Üç kez yazsaydık biri güncellenir ikisi eskirdi.
 *
 * ================================================================
 * SOL PANEL PANEL HAKKINDA TEK KELİME YAZMIYOR
 * ================================================================
 * Alanı dolduran şey sistemin ürettiği programın KENDİSİ: haftalık
 * ızgara, dersler, tamamlanma sayacı. "Öğrenci takip sistemine hoş
 * geldiniz" cümlesi yerine ne yaptığını GÖSTERİYOR.
 *
 * Izgara SEYREK: 70 hücrenin 10'u dolu. Dolu bir ızgara gerçek bir
 * haftaya benzemez — ve arka planda sürekli oynayan bir doku gözü
 * yorar. Boş hücre burada bir boşluk değil, nefes.
 *
 * Her şey TEK 16 SANİYELİK SAATE bağlı: tarama çizgisi, gün
 * segmentleri, sayaç, renk ve yazı değişimi. Negatif gecikmeler
 * (`-14.72s`) her bloğa döngünün kendi konumundaki anı veriyor;
 * çizgi o bloğa vardığında blok tam o sırada yeşile dönüyor.
 * Kare dizileri `tokens.css` içinde.
 *
 * ================================================================
 * DAR EKRANDA IZGARA YOK, KİMLİK VAR — 07.09.2026
 * ================================================================
 * Izgara 700px sabit genişlikte ve içi mutlak konumlu; daraltılınca
 * okunmaz bir şeride dönüyor. O yüzden 1024px altında ÇİZİLMİYOR.
 *
 * Ama önce yerine hiçbir şey konmuyordu ve sonuç şuydu: telefonda
 * giriş ekranı **bomboş beyaz bir sayfada ortada duran bir kutu**.
 * Emre'nin sözü (07.09.2026): *"arka plan beyaz ortada login kutusu
 * istemiyorum."*
 *
 * Dar ekranda artık aynı kimliği taşıyan yatay bir ŞERİT var:
 * aynı gradyan, aynı marka bloğu, aynı yedi günlük tamamlama
 * çubuğu ve aynı sayaç. Izgara gitti, kimlik kaldı.
 *
 * Bunun ikinci sonucu daha önemli: ÖĞRENCİ uygulaması artık bu
 * kabuğu kullanabiliyor. Önce kullanamıyordu, çünkü kabuk telefonda
 * boş bir beyazlıktan ibaretti ve öğrencinin birincil cihazı telefon.
 * İki giriş ekranı bugün aynı yerden çiziliyor — biri güncellenip
 * öteki eskiyemez.
 */

/** Haftanın günleri — çizimdeki sütun konumlarıyla. */
const GUNLER = ["PZT", "SAL", "ÇAR", "PER", "CUM", "CMT", "PAZ"] as const;

/** Yedi sütunun sol konumu (yüzde). Bir sütun 12.5%, aralık 14.285%. */
const SUTUN = [0, 14.285, 28.57, 42.855, 57.14, 71.425, 85.71] as const;

interface Blok {
  /** Sütun sırası — `SUTUN` dizisinin indisi. */
  gun: number;
  ust: number;
  yuk: number;
  saydam: number;
  /** Deneme bloğu turuncu; ders blokları marka mavisi. */
  vurgu?: boolean;
  /** `blokGir` gecikmesi (ms) — çizimde 220'den başlayıp 55 artıyor. */
  gecikme: number;
  /**
   * 16 saniyelik döngüdeki faz. Tarama çizgisi bu bloğa vardığında
   * blok yeşile dönüyor ve (iki ders varsa) yazı değişiyor.
   */
  faz?: number;
  /** 27 saniyelik ikinci saatte bloğun kaybolduğu an. */
  kaybolma?: number;
  /** Tek ders, ya da döngü ortasında değişen iki ders. */
  ders?: string;
  dersIkinci?: string;
}

/** Konum, boyut, saydamlık ve zamanlama çizimden birebir alındı. */
const BLOKLAR: readonly Blok[] = [
  {
    gun: 0,
    ust: 88,
    yuk: 115,
    saydam: 1,
    gecikme: 220,
    faz: -14.72,
    ders: "Matematik",
    dersIkinci: "Geometri",
  },
  { gun: 0, ust: 398, yuk: 53, saydam: 0.26, gecikme: 275 },
  { gun: 1, ust: 212, yuk: 115, saydam: 1, gecikme: 330, faz: -12.43, kaybolma: 9, ders: "Türkçe" },
  { gun: 2, ust: 26, yuk: 53, saydam: 0.26, gecikme: 385 },
  {
    gun: 2,
    ust: 274,
    yuk: 177,
    saydam: 1,
    gecikme: 440,
    faz: -10.15,
    ders: "Fizik",
    dersIkinci: "Kimya",
  },
  { gun: 3, ust: 150, yuk: 115, saydam: 1, gecikme: 495, faz: -7.86, ders: "Koçluk" },
  { gun: 4, ust: 88, yuk: 53, saydam: 0.26, gecikme: 550 },
  {
    gun: 4,
    ust: 336,
    yuk: 115,
    saydam: 1,
    gecikme: 605,
    faz: -5.58,
    kaybolma: 21.5,
    ders: "Biyoloji",
  },
  { gun: 5, ust: 150, yuk: 239, saydam: 1, vurgu: true, gecikme: 660, ders: "TYT Deneme" },
  {
    gun: 6,
    ust: 398,
    yuk: 115,
    saydam: 1,
    gecikme: 715,
    faz: -1.01,
    ders: "Tekrar",
    dersIkinci: "Soru çözümü",
  },
];

/** Sayaç şeridi — sekiz hane, 22px'lik adımlarla kayıyor. */
const SAYAC = ["0", "15", "30", "45", "60", "75", "90", "100"] as const;

function blokAnimasyonu(b: Blok): string {
  const parcalar = [`blokGir var(--sure-normal) var(--ease-cikis) ${b.gecikme}ms backwards`];
  if (b.kaybolma !== undefined) parcalar.push(`blokDon 27s ease-in-out ${b.kaybolma}s infinite`);
  if (b.faz !== undefined) parcalar.push(`renkDon 16s linear ${b.faz}s infinite`);
  return parcalar.join(", ");
}

function YasayanHafta() {
  return (
    <div
      aria-hidden="true"
      className="relative hidden w-[700px] shrink-0 flex-col justify-between overflow-hidden px-14 pt-10 pb-[34px] lg:flex"
      style={{ background: "var(--gradyan-kabuk)" }}
    >
      {/* Haftalık ızgara — üstte gün başlıkları, altında bloklar. */}
      <div className="absolute inset-x-14 top-[122px] bottom-32">
        {GUNLER.map((gun, i) => (
          <span
            key={gun}
            className="text-kabuk-metin-soluk absolute top-0 w-[12.5%] text-[10.5px] font-medium tracking-[0.09em]"
            style={{ left: `${SUTUN[i]}%` }}
          >
            {gun}
          </span>
        ))}

        {BLOKLAR.map((b, i) => (
          <div
            key={i}
            className="absolute w-[12.5%]"
            style={{ left: `${SUTUN[b.gun]}%`, top: b.ust, height: b.yuk, opacity: b.saydam }}
          >
            <div
              className="relative size-full rounded-[6px]"
              style={{
                background: b.vurgu ? "var(--color-vurgu-700)" : "var(--color-marka-600)",
                animation: blokAnimasyonu(b),
              }}
            >
              {b.ders && (
                <span
                  className="text-notr-0 absolute top-[9px] right-[10px] left-[11px] text-[12px] leading-[1.3] font-medium"
                  style={
                    b.dersIkinci !== undefined
                      ? { animation: `yaziA 16s linear ${b.faz ?? 0}s infinite` }
                      : undefined
                  }
                >
                  {b.ders}
                </span>
              )}
              {b.dersIkinci && (
                <span
                  className="text-notr-0 absolute top-[9px] right-[10px] left-[11px] text-[12px] leading-[1.3] font-medium opacity-0"
                  style={{ animation: `yaziB 16s linear ${b.faz ?? 0}s infinite` }}
                >
                  {b.dersIkinci}
                </span>
              )}
            </div>
          </div>
        ))}

        {/* Saat çizgileri. */}
        <div
          className="pointer-events-none absolute inset-x-0 top-[26px] bottom-0"
          style={{
            backgroundImage: "linear-gradient(var(--color-kabuk-izgara) 1px, transparent 1px)",
            backgroundSize: "100% 62px",
          }}
        />

        {/*
          Tarama çizgisi. Haftayı soldan sağa geçiyor ve geçtiği günün
          bloğunu yeşile döndürüyor — "bu hafta buraya kadar tamam"
          bilgisini animasyonun kendisi taşıyor.
        */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-px"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, var(--color-kabuk-tarama) 22%, var(--color-kabuk-tarama) 78%, transparent 100%)",
            boxShadow: "var(--golge-tarama)",
            animation: "tarama 16s linear infinite",
          }}
        />
      </div>

      {/* Sol üstten gelen ışık — panelin köşesini boş bırakmıyor. */}
      <div
        className="pointer-events-none absolute -top-[220px] -left-[140px] size-[660px] rounded-full"
        style={{ background: "var(--gradyan-kabuk-isik)" }}
      />

      <div className="relative flex items-center gap-3">
        <div
          className="rounded-kart bg-marka-600 text-notr-0 grid size-10 place-items-center text-[17px] font-bold"
          style={{ animation: "isaretOtur var(--sure-normal) var(--ease-yay) backwards" }}
        >
          {marka.kisaAd.slice(0, 1)}
        </div>
        <span className="text-notr-0 text-[15px] font-semibold tracking-[-0.01em]">
          {marka.kisaAd}
        </span>
      </div>

      <div className="relative flex items-end justify-between">
        <div className="relative flex flex-col gap-[9px]">
          <span className="text-kabuk-metin-soluk text-[10.5px] font-semibold tracking-[0.11em]">
            HAFTALIK TAMAMLAMA
          </span>
          <div className="flex items-center gap-[13px]">
            <div className="flex items-baseline gap-0.5">
              <div className="h-[22px] overflow-hidden">
                <div style={{ animation: "sayacKay 16s steps(8, end) infinite" }}>
                  {SAYAC.map((s) => (
                    <span
                      key={s}
                      className="text-notr-0 block h-[22px] text-[20px] leading-[22px] font-semibold"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
              <span className="text-kabuk-metin text-[13px] font-medium">%</span>
            </div>

            {/*
              ================================================================
              YEDİ GÜN ÇUBUĞU — HER BİRİ KENDİ SIRASINDA DOLUYOR
              ================================================================
              `scale-x-0` SINIFI KALDIRILDI (07.09.2026). Çubuklar
              hiç dolmuyordu ve sebebi Tailwind v3 → v4 anlam
              değişikliğiydi:

                v3'te `scale-x-0` -> transform: scaleX(0)
                v4'te `scale-x-0` -> scale: 0 1        (AYRI ÖZELLİK)

              CSS `scale` ile `transform`u ÇARPIYOR. Animasyon
              `transform: scaleX(1)`e ulaşsa bile sonuç 0 × 1 = 0
              kalıyordu. Tarayıcıda ölçüldü:

                animationName  seg0
                transform      matrix(1, 0, 0, 1, 0, 0)   ← animasyon çalıştı
                scale          "0 1"                       ← sınıf sıfırladı
                genişlik       0px

              Hiçbir hata yok, animasyon gerçekten koşuyor — yalnızca
              görünmüyor. `belirtec-yazimi.test.ts`in yakaladığı
              `duration-[--sure-hizli]` ile birebir aynı sınıf: v3
              sözdizimi v4'te sessizce başka bir şey ifade ediyor.

              Başlangıç durumu artık animasyonun kendi %0 karesinden
              geliyor (`backwards`): çubuk zaten boş başlıyor.
            */}
            <div className="flex gap-1">
              {GUNLER.map((gun, i) => (
                <div key={gun} className="bg-kabuk-hover h-[5px] w-6 overflow-hidden rounded-full">
                  <div
                    className="bg-onay-600 size-full origin-left rounded-full"
                    style={{ animation: `seg${i} 16s linear infinite backwards` }}
                  />
                </div>
              ))}
            </div>

            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-basarili-500)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ animation: "tikGel 16s linear infinite" }}
            >
              <path d="m5 13 4 4L19 7" />
            </svg>
          </div>
        </div>

        <div className="text-kabuk-metin-soluk flex flex-col items-end gap-[3px] text-[12px]">
          {/*
            Sürüm derleme anında `package.json`dan geliyor (vite.config).
            Gelmediyse satır hiç çizilmiyor: yanlış bir sürüm numarası,
            hiç sürüm göstermemekten kötü — destek ekibi ona bakarak
            karar veriyor.
          */}
          {marka.surum && <span>Sürüm {marka.surum}</span>}
          <span>{marka.destekEposta}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * DAR EKRAN ŞERİDİ — ızgaranın yerine geçen kimlik bandı.
 *
 * İçindeki her parça soldaki panelden alındı ve AYNI değişkenlerden
 * besleniyor: `--gradyan-kabuk`, marka bloğu, yedi günlük çubuk,
 * 16 saniyelik sayaç. Ayrı bir tasarım değil, aynı tasarımın dar
 * hâli — panel değişirse burası da değişmeli diye tek dosyada
 * duruyorlar.
 *
 * `aria-hidden`: soldaki panel gibi tamamen dekoratif. Ekran okuyucu
 * için giriş ekranı formdan ibaret.
 */
function HaftaSeridi() {
  return (
    <div
      aria-hidden="true"
      className="relative flex shrink-0 flex-col gap-4 overflow-hidden px-6 pt-7 pb-6 lg:hidden"
      style={{ background: "var(--gradyan-kabuk)" }}
    >
      <div className="flex items-center gap-2.5">
        <div className="rounded-alan bg-marka-600 text-notr-0 grid size-8 shrink-0 place-items-center text-sm font-semibold">
          {marka.kisaAd.slice(0, 1)}
        </div>
        <span className="text-notr-0 text-[15px] font-semibold tracking-[-0.01em]">
          {marka.kisaAd}
        </span>
      </div>

      {/*
        Gün başlıkları — soldaki panelin ızgarasından. Şeritte ızgara
        yok ama günler var: bandın "bir HAFTA" anlattığını söyleyen
        tek şey bu satır.
      */}
      <div className="flex justify-between">
        {GUNLER.map((gun) => (
          <span
            key={gun}
            className="text-kabuk-metin-soluk text-[10.5px] font-medium tracking-[0.09em]"
          >
            {gun}
          </span>
        ))}
      </div>

      <div className="flex flex-col gap-[9px]">
        <span className="text-kabuk-metin-soluk text-[10.5px] font-semibold tracking-[0.11em]">
          HAFTALIK TAMAMLAMA
        </span>
        <div className="flex items-center gap-[13px]">
          <div className="flex items-baseline gap-0.5">
            <div className="h-[22px] overflow-hidden">
              <div style={{ animation: "sayacKay 16s steps(8, end) infinite" }}>
                {SAYAC.map((s) => (
                  <span
                    key={s}
                    className="text-notr-0 block h-[22px] text-[20px] leading-[22px] font-semibold"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <span className="text-kabuk-metin text-[13px] font-medium">%</span>
          </div>

          {/*
            Çubuklarda `scale-x-0` YOK — Tailwind v4'te o sınıf ayrı
            bir `scale` özelliği yazıyor ve `transform`u sıfırla
            çarpıp animasyonu görünmez kılıyor (soldaki panelde
            07.09.2026'da ölçülerek bulundu).
          */}
          <div className="flex flex-1 gap-1">
            {GUNLER.map((gun, i) => (
              <div key={gun} className="bg-kabuk-hover h-[5px] flex-1 overflow-hidden rounded-full">
                <div
                  className="bg-onay-600 size-full origin-left rounded-full"
                  style={{ animation: `seg${i} 16s linear infinite backwards` }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Başlığın üstündeki 54px'lik ikon kutusu — çizimde durum ekranlarında var. */
const ISARET_TONU: Record<NotTonu, string> = {
  bilgi: "bg-marka-50 text-marka-700 ring-marka-200",
  onay: "bg-onay-50 text-onay-700 ring-onay-200",
  uyari: "bg-uyari-50 text-uyari-800 ring-uyari-200",
  hata: "bg-hata-50 text-hata-700 ring-hata-200",
};

export interface GirisKabuguProps {
  baslik: string;
  aciklama: ReactNode;
  /**
   * Başlığın üstündeki ikon kutusu.
   *
   * Çizimde yalnızca DURUM ekranlarında var (çıkış yapıldı, hesap
   * kilitli, oturum düştü), form ekranlarında yok. Ayrım kasıtlı:
   * form ekranında kullanıcı bir iş yapmaya geldi ve ikon onu
   * geciktirir; durum ekranında ise ne olduğunu bir bakışta
   * anlaması gerekiyor.
   */
  isaret?: { ikon: IkonAdi; ton: NotTonu };
  /**
   * Üstten çizgili alt not. Çizimde her üç ekranda da var ve
   * hepsinde aynı işi yapıyor: kullanıcının yanlış çıkaracağı
   * sonucu önceden düzeltiyor.
   */
  altNot?: ReactNode;
  /** Form. */
  children: ReactNode;
}

export function GirisKabugu({ baslik, aciklama, isaret, altNot, children }: GirisKabuguProps) {
  /*
    `.sirali` sıra numarasını `--sira` değişkeninden okuyor ve bu
    bileşenin çocukları DIŞARIDAN geliyor — numarayı burada veriyoruz.
    Vermeseydik hepsi 0 olur ve sıralı beliriş tek bir sıçramaya
    dönerdi; sıranın kendisi bilgi taşıyor (önce başlık, sonra
    alanlar, en son not).
  */
  let sira = 1;
  const numarali = Children.map(children, (cocuk) => {
    if (!isValidElement<{ style?: CSSProperties }>(cocuk)) return cocuk;
    return cloneElement(cocuk, {
      style: { ...cocuk.props.style, ["--sira" as string]: sira++ },
    });
  });

  return (
    /*
      Dar ekranda SÜTUN, geniş ekranda SATIR: şerit üstte, ızgara
      solda. İkisi aynı `flex` ile çözülüyor — ayrı bir yerleşim
      yazsaydık biri değiştiğinde öteki sessizce ayrılırdı.
    */
    <div className="bg-notr-0 flex min-h-dvh flex-col lg:flex-row">
      <HaftaSeridi />
      <YasayanHafta />

      {/*
        Dar ekranda form ŞERİDİN ALTINA yaslanıyor (`items-start`),
        geniş ekranda dikeyde ortalanıyor. Telefonda ortalasaydık
        bandın altında büyük bir boşluk kalır ve sayfa yine "beyaz
        zeminde havada duran bir kutu" gibi okunurdu — düzeltilmek
        istenen şeyin ta kendisi.
      */}
      <div className="flex flex-1 flex-col items-center justify-start px-6 pt-9 pb-6 lg:justify-center lg:px-16 lg:py-[54px]">
        <div className="sirali flex w-full max-w-[396px] flex-col gap-5">
          {isaret && (
            <div
              aria-hidden="true"
              className={cn(
                "rounded-kart grid size-[54px] place-items-center ring-1 ring-inset",
                ISARET_TONU[isaret.ton],
              )}
              style={{
                ["--sira" as string]: 0,
                animation: "isaretOtur var(--sure-normal) var(--ease-yay) backwards",
              }}
            >
              <Ikon ad={isaret.ikon} boyut="kucuk" />
            </div>
          )}

          <div className="flex flex-col gap-1.5" style={{ ["--sira" as string]: 0 }}>
            <h1 className="text-notr-900 text-[25px] font-semibold tracking-[-0.015em]">
              {baslik}
            </h1>
            <div className="text-notr-600 text-[14.5px] leading-[1.6]">{aciklama}</div>
          </div>

          {numarali}

          {altNot && (
            <div
              className="border-notr-100 text-notr-600 border-t pt-3.5 text-[12.5px] leading-[1.6]"
              style={{ ["--sira" as string]: sira }}
            >
              {altNot}
            </div>
          )}
        </div>

        {/*
          KÜNYE — dar ekranda sayfanın dibinde, geniş ekranda YOK.
          Geniş ekranda aynı bilgi soldaki panelin altında zaten
          duruyor; iki kez yazmak onu bir gün ayrıştırırdı.
        */}
        <div className="text-notr-600 mt-auto flex flex-col items-center gap-0.5 pt-10 text-[12px] lg:hidden">
          {marka.surum && <span>Sürüm {marka.surum}</span>}
          <span>{marka.destekEposta}</span>
        </div>
      </div>
    </div>
  );
}
